import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang, pick } from "../context/LanguageContext";
import { t } from "../data/translations";
import { resolveImg } from "../lib/api";
import { ArrowDown } from "lucide-react";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 + i * 0.12 },
  }),
};

export const Hero = ({ settings }) => {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const tagline = pick(settings?.tagline, lang);
  const words = tagline.split(" ");
  const line1 = words.slice(0, Math.ceil(words.length / 2)).join(" ");
  const line2 = words.slice(Math.ceil(words.length / 2)).join(" ");

  return (
    <section id="top" ref={ref} className="relative min-h-[100svh] w-full overflow-hidden" data-testid="hero">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={resolveImg(settings?.hero_bg)} alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/55 to-brand-bg/70" />
      <div className="absolute inset-0 bg-brand-bg/25" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 min-h-[100svh] flex flex-col justify-center pt-24 pb-32">
        {/* Dynamic logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex w-fit items-center rounded-3xl bg-white px-6 py-4 mb-8 shadow-[0_18px_50px_rgba(0,0,0,0.5)]"
          data-testid="hero-logo"
        >
          <img
            src={resolveImg(settings?.logo_url)}
            alt={settings?.business_name}
            className="h-20 sm:h-28 w-auto max-w-[300px] object-contain"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-brand-gold uppercase tracking-[0.35em] text-xs sm:text-sm mb-5"
        >
          {pick(settings?.hero_kicker, lang)}
        </motion.p>

        <h1 className="font-display font-black tracking-tight text-[13vw] sm:text-7xl lg:text-8xl leading-[0.95] max-w-5xl">
          <span className="block overflow-hidden">
            <motion.span custom={0} variants={lineReveal} initial="hidden" animate="show" className="block">
              {line1}
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span custom={1} variants={lineReveal} initial="hidden" animate="show" className="block text-brand-primary italic">
              {line2}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-7 max-w-xl text-lg text-brand-muted leading-relaxed text-balance"
        >
          {pick(settings?.hero_subtitle, lang)}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={settings?.doordash_url || "#"}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-order-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-brand-primary hover:bg-brand-primaryHover px-8 py-4 text-base font-semibold transition-[transform,background-color] duration-300 hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(200,76,50,0.35)]"
          >
            {pick(t.hero.order, lang)}
          </a>
          <a
            href="#menu"
            data-testid="hero-menu-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-brand-gold px-8 py-4 text-base font-semibold transition-colors duration-300"
          >
            {pick(t.hero.viewMenu, lang)}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-brand-muted"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{pick(t.hero.scroll, lang)}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};
